// Staff page: the dashboard (who talked, what it cost, the PDFs we shared) plus upload and the daily cost table.
import { createDash } from "./dash.js";

const $ = (id) => document.getElementById(id);
const KEY = "pdfTutorStaffKey";
let key = "";
try {
  key = localStorage.getItem(KEY) || "";
} catch {
  /* storage blocked */
}

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

async function api(path, opts = {}) {
  const res = await fetch(path, { ...opts, headers: { ...(opts.headers || {}), "x-admin-key": key } });
  const body = await res.json().catch(() => ({}));
  if (res.status === 401) {
    showLogin("That staff key is wrong.");
    throw new Error(body.error || "Wrong staff key");
  }
  if (!res.ok) throw new Error(body.error || `Error ${res.status}`);
  return body;
}

function showLogin(msg = "") {
  $("login").hidden = false;
  $("app").hidden = true;
  $("logout").hidden = true;
  $("loginErr").textContent = msg;
}

async function showApp() {
  $("login").hidden = true;
  $("app").hidden = false;
  $("logout").hidden = !key; // no sign-out when the staff page is open to all
  await refresh();
}

const inr = (v) => `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const tok = (v) => Number(v).toLocaleString("en-IN");

async function showUsage() {
  const u = await api("/api/admin/usage?days=7");
  const t = u.total || { inr: 0, minutes: 0, sessions: 0, students: 0, inrPerMinute: 0, from: null };
  const rows = u.days
    .map((d, i) => {
      const label = i === 0 ? "Today" : i === 1 ? "Yesterday" : d.day;
      return `<tr><td>${label}</td><td>${d.sessions}</td><td>${d.minutes}</td><td><b>${inr(d.inr)}</b></td></tr>`;
    })
    .join("");
  const since = t.from ? new Date(`${t.from}T00:00:00+05:30`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";
  $("usage").innerHTML = `
    <div class="bill">
      <div class="bill-total">
        <span class="bill-label">Total cost so far</span>
        <b class="bill-amount">${inr(t.inr)}</b>
        <span class="bill-sub"><b>${t.minutes}</b> minutes talked at <b>${inr(t.costPerMinute)}</b> a minute${t.pdfs ? ` · <b>${tok(t.pdfs)}</b> PDFs at ${inr(t.costPerPdf)} each` : ""}</span>
        <span class="muted small">${since ? `since ${esc(since)} · ` : ""}${tok(t.sessions)} conversations on this tutor · ${tok(t.students)} students</span>
      </div>
      <p class="muted small">Nothing has actually been billed yet: the Google key is on the free tier. This is what it costs us at ${inr(t.costPerMinute)} a minute.</p>
    </div>
    <table class="usage">
      <thead><tr><th>Day</th><th>Conversations</th><th>Minutes</th><th>Cost</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
}

// staff routes want the key in a header, so a plain link cannot download them
async function download(url, filename) {
  if (!key) {
    location.href = url;
    return;
  }
  const res = await fetch(url, { headers: { "x-admin-key": key } });
  if (!res.ok) throw new Error(`Error ${res.status}`);
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 5000);
}

// the few lines of news that are newer than the PDF: the interviewer reads them as the later word
async function loadNews() {
  try {
    const n = await api("/api/admin/news");
    $("newsText").value = n.text || "";
    $("newsBy").value = n.by || "";
    $("newsAge").textContent = !n.updatedAt
      ? "nothing yet"
      : n.daysOld === 0
        ? "updated today"
        : n.daysOld === 1
          ? "updated yesterday"
          : `updated ${n.daysOld} days ago${n.daysOld > 10 ? " — getting old" : ""}`;
  } catch {
    $("newsAge").textContent = "";
  }
}

$("newsSave").onclick = async () => {
  $("newsSave").disabled = true;
  $("newsMsg").textContent = "Saving…";
  try {
    await api("/api/admin/news", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: $("newsText").value, by: $("newsBy").value }),
    });
    $("newsMsg").textContent = $("newsText").value.trim() ? "Saved — the interviewer is using it now." : "Cleared.";
    await loadNews();
  } catch (e) {
    $("newsMsg").textContent = `Could not save: ${e.message}`;
  }
  $("newsSave").disabled = false;
};

const dash = createDash({
  api,
  download,
  $,
  copy: (text) => (navigator.clipboard ? navigator.clipboard.writeText(text) : Promise.reject(new Error("This browser will not let the page copy."))),
});

dash.on("qr", (m) => {
  const src = `/api/pdfs/${encodeURIComponent(m.slug)}/qr.svg`;
  $("qrTitle").textContent = m.title;
  $("qrImg").src = src;
  $("qrUrl").textContent = m.url;
  $("qrDownload").href = src;
  $("qrDownload").download = `${m.slug}-qr.svg`;
  $("qrDlg").showModal();
});

dash.on("replaced", (m) => {
  alert(`Done. “${m.title}” is now ${m.pages} pages (it was ${m.was}). The student link has not changed:\n${m.url}`);
});

dash.on("del", (m) => {
  if (!confirm(`Delete “${m.title}”? The student link will stop working.`)) return;
  api(`/api/admin/pdfs/${encodeURIComponent(m.slug)}`, { method: "DELETE" })
    .then(refresh)
    .catch(() => {});
});

async function refresh() {
  showUsage().catch(() => ($("usage").innerHTML = '<p class="muted">Usage not available.</p>'));
  loadNews();
  await dash.refresh();
}

function pick(file) {
  if (!file) return;
  if (!/\.pdf$/i.test(file.name) && file.type !== "application/pdf") {
    $("fileName").textContent = "Only PDF files, please";
    return;
  }
  pick.file = file;
  $("fileName").textContent = `${file.name} · ${(file.size / 1048576).toFixed(1)} MB`;
  $("uploadBtn").disabled = false;
}

function upload(e) {
  e.preventDefault();
  const file = pick.file;
  if (!file) return;
  const fd = new FormData();
  fd.append("title", $("titleIn").value.trim());
  fd.append("file", file);
  const xhr = new XMLHttpRequest();
  xhr.open("POST", "/api/admin/pdfs");
  xhr.setRequestHeader("x-admin-key", key);
  $("prog").hidden = false;
  $("uploadBtn").disabled = true;
  $("upMsg").textContent = "Uploading…";
  xhr.upload.onprogress = (ev) => {
    if (!ev.lengthComputable) return;
    $("prog").firstElementChild.style.width = `${Math.round((ev.loaded / ev.total) * 100)}%`;
    if (ev.loaded === ev.total) $("upMsg").textContent = "Reading the pages…";
  };
  xhr.onload = () => {
    $("prog").hidden = true;
    let body = {};
    try {
      body = JSON.parse(xhr.responseText);
    } catch {
      /* not json */
    }
    if (xhr.status === 201) {
      $("upMsg").innerHTML = `Ready: <a href="${esc(body.url)}" target="_blank" rel="noopener">${esc(body.title)}</a> (${body.pages} pages)`;
      pick.file = null;
      $("file").value = "";
      $("titleIn").value = "";
      $("fileName").textContent = "Any study PDF in Hindi or English, up to 100 MB";
      refresh();
    } else {
      $("upMsg").textContent = body.error || `Upload failed (${xhr.status})`;
      $("uploadBtn").disabled = false;
    }
  };
  xhr.onerror = () => {
    $("prog").hidden = true;
    $("upMsg").textContent = "Upload failed. Check the internet and try again.";
    $("uploadBtn").disabled = false;
  };
  xhr.send(fd);
}

$("loginForm").addEventListener("submit", async (e) => {
  e.preventDefault();
  key = $("key").value.trim();
  const ok = await fetch("/api/admin/check", { headers: { "x-admin-key": key } });
  if (!ok.ok) return showLogin(ok.status === 503 ? "The server has no staff key set (ADMIN_KEY)." : "That staff key is wrong.");
  try {
    localStorage.setItem(KEY, key);
  } catch {
    /* storage blocked */
  }
  showApp();
});
$("logout").onclick = () => {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* storage blocked */
  }
  key = "";
  showLogin();
};
$("file").addEventListener("change", (e) => pick(e.target.files[0]));
const zone = $("zone");
zone.addEventListener("dragover", (e) => {
  e.preventDefault();
  zone.classList.add("over");
});
zone.addEventListener("dragleave", () => zone.classList.remove("over"));
zone.addEventListener("drop", (e) => {
  e.preventDefault();
  zone.classList.remove("over");
  pick(e.dataTransfer.files[0]);
});
$("uploadForm").addEventListener("submit", upload);

// the staff page may be open (no key needed); otherwise use the saved key or ask for it
fetch("/api/admin/check", { headers: key ? { "x-admin-key": key } : {} })
  .then((r) => (r.ok ? showApp() : showLogin()))
  .catch(() => showLogin());
